import { Chakra } from "@airhelp/react-ui-components";
import { stickyBottomBarFirstTriggerStyles } from "components/StickyBottomBar";
import StickyBottomBarTrigger from "components/StickyBottomBar/StickyBottomBarTrigger";
import config from "config/config";
import { DataContextProvider } from "contexts/DataContext";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { MobileMenuContextProvider } from "contexts/MobileMenuContext";
import { ModalContextProvider } from "contexts/ModalContext";
import { StickyBottomBarContextProvider } from "contexts/StickyBottomBarContext";
import CustomHead from "modules/common/components/CustomHead";
import Footer from "modules/common/components/Footer";
import Grid from "modules/common/components/Grid";
import HeaderNav from "modules/common/components/HeaderNav";
import CheckCompensationButton from "modules/common/components/HeaderNav/CheckCompensationButton";
import MenuItems from "modules/common/components/HeaderNav/MenuItems";
import PressLogos from "modules/common/components/PressLogos";
import { DynamicComponent } from "modules/storyblok/DynamicComponent";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { getStoryLastUpdatedDate } from "utils/getStoryLastUpdatedDate";
import { useTopMenu } from "utils/menuUtils";
import StickyBottomBarAdapter from "../adapters/StickyBottomBar";
import { BlogFeaturedImage, BlogSubscribeBox, SocialLinksPanel } from "../components";

const { Box, Flex, Heading, Stack, Divider } = Chakra;

// TODO | Migrate the template to TS after AhPlus migration

const BlogPostTemplate = ({ story, cmsLanguagesData, serverSideStoryblokPages, whiteListDomains }) => {
  const {
    name,
    content: {
      metaTitle,
      metaDescription,
      author,
      featuredImage,
      body,
      stickyBottomBarHeading,
      hideStickyBottomBar,
      stickyBottomBarVariant,
    },
  } = story;

  const { query } = useRouter();
  const language = useLanguage();
  const translations = useTranslations();
  const topMenu = useTopMenu(language);
  const lastUpdatedDate = getStoryLastUpdatedDate(story, language);
  const url = `${config.websiteUrl}/${query.language}/blog/${query.slug}`;
  const blogpostCaption = author
    ? `${translations("by_author_name")} ${author}・${translations("last_updated")} ${lastUpdatedDate}`
    : `${translations("last_updated")} ${lastUpdatedDate}`;

  return (
    <DataContextProvider data={{ cmsLanguagesData, serverSideStoryblokPages, whiteListDomains }}>
      <CustomHead title={metaTitle} description={metaDescription} />
      <MobileMenuContextProvider>
        <ModalContextProvider>
          <HeaderNav menuItems={<MenuItems menu={topMenu} />} headerButtons={<CheckCompensationButton />} />
          <Grid pt={32} as="main">
            <Box gridColumn={{ base: "initial", md: "2 / span 10", lg: "3 / span 8" }}>
              <BlogFeaturedImage src={featuredImage} alt={name} />
              <Heading variant="h1" as="h1" mb={6}>
                {name}
              </Heading>
              <Box mb={10} color="greyscale.500" fontSize="lg">
                {blogpostCaption}
              </Box>
              <Stack spacing={6}>
                <StickyBottomBarTrigger type="first" styles={stickyBottomBarFirstTriggerStyles} />
                {body ? body.map((blok) => <DynamicComponent blok={blok} key={blok._uid} />) : null}
              </Stack>
              <BlogSubscribeBox />
              <Divider mb={16} pt={16} />
              <Flex direction={{ base: "column", lg: "row" }} alignItems="center" justifyContent="space-between">
                <Heading variant="h3" as="h3" mb={{ base: 7, lg: 0 }}>
                  {translations("share_it_with_your_friends")}
                </Heading>
                <SocialLinksPanel url={url} title={name} />
              </Flex>
              <SocialLinksPanel
                direction="column"
                url={url}
                title={name}
                pos="fixed"
                top={28}
                left={16}
                display={{ base: "none", lg: "flex" }}
              />
            </Box>
          </Grid>
          <PressLogos />
          <Footer />
        </ModalContextProvider>
      </MobileMenuContextProvider>
      <StickyBottomBarAdapter
        showStickyBottomBar={!hideStickyBottomBar}
        stickyBottomBarVariant={stickyBottomBarVariant}
        stickyBottomBarHeading={stickyBottomBarHeading}
      />
    </DataContextProvider>
  );
};

const BlogPostTemplateAdapter = (props) => (
  <StickyBottomBarContextProvider threshold={0.4}>
    <BlogPostTemplate {...props} />
  </StickyBottomBarContextProvider>
);

export default BlogPostTemplateAdapter;

BlogPostTemplate.propTypes = {
  story: PropTypes.shape({
    name: PropTypes.string,
    content: PropTypes.shape({
      body: PropTypes.arrayOf(PropTypes.shape({})),
      metaTitle: PropTypes.string,
      metaDescription: PropTypes.string,
      author: PropTypes.string,
      featured: PropTypes.bool,
      order: PropTypes.number,
      featuredImage: PropTypes.string,
    }),
  }).isRequired,
  cmsLanguagesData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      slug: PropTypes.string,
      full_slug: PropTypes.string,
      is_folder: PropTypes.bool,
      parent_id: PropTypes.number,
    })
  ),
  whiteListDomains: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
      dimension_value: PropTypes.string,
    })
  ),
  serverSideStoryblokPages: PropTypes.shape({}).isRequired,
};

BlogPostTemplate.defaultProps = {
  cmsLanguagesData: [],
  whiteListDomains: [],
};
